import React from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import ReactMarkdown from "react-markdown";
import EventFinished from "src/components/EventFinished";

const JackpotParty = () => {
  const { i18n, t } = useTranslation();
  const table = t("jackpotParty.prizesTable", { returnObjects: true });

  return (
    <PromoDetailDiv>
      <EventFinished scheduleKeyName="jackpotParty" eventType="promotion" />
      <PartOne>
        <ResponsiveImage
          src={`promotions/header_jp_june_2024_${i18n.language}.jpg`}
          alt="walk_of_fame_2023_header_desktop"
        />
        <ResponsiveImage
          src={`promotions/header_mobile_jp_june_2024_${i18n.language}.jpg`}
          alt="walk_of_fame_2023_header_mobile"
        />
      </PartOne>
      <container-div>
        <PartTwo>
          <React.Fragment>
            <h1>{t("jackpotParty.introSection.title")}</h1>
            <h2>{t("jackpotParty.introSection.subtitle")}</h2>
            <p>
              <strong>{t("jackpotParty.introSection.hours")}</strong>
            </p>
            <ReactMarkdown>{t("jackpotParty.introSection.text")}</ReactMarkdown>
          </React.Fragment>
          <TableDiv>
            <table>
              <thead>
                <tr>
                  <th colSpan="2">{table.title}</th>
                </tr>
                <tr>
                  <th>{table.header[0]}</th>
                  <th>{table.header[1]}</th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, idx) =>
                  row.prizes.map((prize, prizeIdx) => (
                    <tr key={row.time + prizeIdx}>
                      {prizeIdx === 0 && (
                        <td rowSpan={row.prizes.length}>{row.time}</td>
                      )}
                      <td>
                        <ReactMarkdown>{prize}</ReactMarkdown>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableDiv>
          <p>{t("jackpotParty.draws")}</p>
        </PartTwo>
      </container-div>
      <PartThree>
        <div>
          <div>
            <h2>{t("jackpotParty.rules.title")}</h2>
            <ReactMarkdown>{t("jackpotParty.rules.list")}</ReactMarkdown>
            <p>
              <ReactMarkdown>{t("jackpotParty.rules.footer")}</ReactMarkdown>
            </p>
          </div>
        </div>
      </PartThree>
    </PromoDetailDiv>
  );
};
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const PromoDetailDiv = styled.div`
  height: fit-content;
  width: 100%;
  background: var(--white);
  font-family: "Poppins", sans-serif;
  @media screen and (min-width: 992px) {
    /* start of desktop styles */
  }
  @media screen and (min-width: 480px) and (max-width: 991px) {
    /* start of medium tablet styles */
  }
  @media screen and (max-width: 479px) {
    /* start of phone styles */
  }
`;
const PartOne = styled.div`
  height: 100%;
  width: 100%;
  &::after {
    top: 0;
    left: 0;
    z-index: -1;
    width: 100%;
    height: 100%;
    content: " ";
    position: absolute;
    background: rgba(0, 0, 0, 0.2);
  }
  & > img {
    width: 100%;
    height: 100%;
  }
  @media screen and (min-width: 992px) {
    /* start of desktop styles */
    & > img:first-child {
      display: block;
    }
    & > img:nth-child(2) {
      display: none;
    }
  }
  @media screen and (max-width: 991px) {
    /* start of phone and medium tablet styles */
    & > img:first-child {
      display: none;
    }
    & > img:nth-child(2) {
      display: block;
      padding-top: 80px;
    }
  }
`;
const PartTwo = styled.div`
  text-align: center;
  max-width: 1440px;
  width: 100%;
  margin: 40px auto 60px auto;
  height: auto;
  & > h1:first-child {
    font-size: 45px;
    font-weight: 800;
    line-height: 1.15em;
    margin-bottom: 0;
  }
  & > p {
    font-size: 18px;
    text-align: center;
    margin: 5px 0;
  }
  .special {
    color: var(--red);
    margin: 20px auto;
    font-size: 24px;
    font-weight: 700;
  }
  @media screen and (max-width: 991px) {
    /* start of phone and medium tablet styles */
    margin: 0;
    padding: 40px 20px;
    & > h1:first-child {
      font-size: 30px;
    }
    & > p {
      font-size: 16px;
    }
  }
`;
const PartThree = styled.div`
  width: 100%;
  height: auto;
  position: relative;
  padding: 40px 0;
  background: var(--lightblack);
  ul {
    margin: 20px auto;
    list-style: decimal;
  }
  & > div:last-child {
    height: auto;
    max-width: 1440px;
    margin: 40px auto;
    width: 100%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    & > div {
      background: var(--white);
      animation: ${fadeIn} 0.3s linear;
      width: 100%;
      height: fit-content;
      padding: 15px;
      & > h2 {
        text-align: center;
        font-size: 22px;
        font-weight: 700;
        line-height: 33px;
      }
      & > ol {
        font-size: 15px;
        line-height: 28px;
        margin-top: 0;
        margin-bottom: 10px;
      }
      & > h3 {
        color: #555;
        margin: 20px 0 0;
        font-weight: 600;
        font-size: 19px;
        line-height: 27px;
      }
      & > p:last-child {
        font-size: 16px;
        padding: 0;
        margin: 0;
        line-height: 28px;
        text-align: center;
      }
    }
  }
  @media screen and (max-width: 991px) {
    /* start of phone and medium tablet styles */
    padding: 20px;
    & > ul:first-child {
      flex-flow: wrap;
      justify-content: space-evenly;
      & > li {
        font-size: 14px;
        max-width: 45%;
      }
    }
    & > div:last-child {
      & > div {
        & > ol {
          font-size: 15px;
          padding-left: 20px;
        }
      }
    }
  }
`;
const TableDiv = styled.div`
  height: fit-content;
  width: 100%;
  margin: 20px 0;
  & > table:first-child {
    width: 100%;
    margin-bottom: 15px;
    background-color: transparent;
    border-spacing: 0;
    border-collapse: collapse;
    text-align: center;
    & > thead {
      & > tr:first-child {
        & > th {
          background: radial-gradient(
            circle,
            rgb(220 220 220) 0%,
            rgb(145 145 145) 50%,
            rgb(204 204 204) 100%,
            rgb(209 209 209) 100%
          );
          color: #000;
          font-family: "Poppins", sans-serif;
          font-weight: 700;
          padding: 10px 5px;
          font-size: 18px;
        }
      }
      & > tr:last-child {
        & > th {
          background: #000;
          color: #fff;
          font-family: "Poppins", sans-serif;
          font-weight: 700;
          padding: 10px 5px;
          font-size: 18px;
        }
      }
    }
    & > tbody {
      & > tr {
        & > td {
          color: #fff;
          background: #000;
          font-family: "Poppins", sans-serif;
          font-weight: 700;
          padding: 15px 5px;
          font-size: 18px;
          border: 2px solid #fff;
          width: 50%;
        }
      }
    }
  }
`;
export default JackpotParty;
